import { Box } from "@mui/system"
import { CircularProgress, Typography } from "@mui/material"
import { CardHolderMenu } from "layouts/card-holder/table/cardHolderMenu.tsx"
import { DriverInfo, VehicleInfo } from "types/list-card-holder"
import Truck from "assets/svg/truck.svg?react"
import MoreVertIcon from "@mui/icons-material/MoreVert"
import { useCardHolderContext } from "layouts/card-holder/context/useCardHolderContext.ts"
import {
    ActiveDriverIcon,
    InactiveDriverIcon,
} from "layouts/card-holder/drivers/icons.tsx"

const CardHolderAvatar = ({
    isVehicle,
    active,
}: {
    isVehicle: boolean
    active: boolean
}) => {
    if (isVehicle) {
        return (
            <Truck style={{ color: "#003936", width: "3rem", paddingTop: "4px" }} />
        )
    }
    return active ? <ActiveDriverIcon /> : <InactiveDriverIcon />
}

const RowAction = ({
    isUpdating,
    isSelected,
    dataCyPrefix,
    driverId,
    onSelect,
}: {
    isUpdating: boolean
    isSelected: boolean
    dataCyPrefix: string
    driverId: string
    onSelect: () => void
}) => {
    if (isUpdating) {
        return (
            <CircularProgress
                size={22}
                color="primary"
                data-testid={`driver-menu-loader-${driverId}`}
                data-cy={`${dataCyPrefix}__${driverId}--menu-loader`}
            />
        )
    }

    return (
        <MoreVertIcon
            sx={{
                p: 0,
                m: 0,
                height: "0.5rem",
                width: "0.5rem",
                transform: "scale(3.5)",
                color: isSelected ? "secondary.main" : "grey.600",
                transition: "color 0.2s ease-in-out, opacity 0.2s ease-in-out",
                "&:hover": {
                    color: "secondary.main",
                    opacity: isSelected ? 0.5 : 1,
                },
                cursor: "pointer",
            }}
            onClick={onSelect}
            data-testid={`driver-menu-${driverId}`}
            data-cy={`${dataCyPrefix}__${driverId}--menu`}
        />
    )
}

export const CardHolderNameCell = ({
    driver,
}: {
    driver: DriverInfo | VehicleInfo
}) => {
    const {
        setDriverSelected,
        driverSelected,
        isVehicle,
        isRefetchingList,
        updatedCardHolderId,
    } = useCardHolderContext()

    const isSelected = !!driverSelected && driverSelected.id === driver.id

    // While the GET /v2/card-holders triggered after the update is not
    // finished, replace this row's MoreVertIcon with a loader.
    const isUpdating = isRefetchingList && updatedCardHolderId === driver.id

    const dataCyPrefix = isVehicle ? "vehicle" : "driver"
    const label = isVehicle
        ? (driver as VehicleInfo).description
        : (driver as DriverInfo).name

    // Dim non-selected rows when a menu is open (but not during the refetch)
    // or when the row is inactive.
    const isDimmed =
        ((!!driverSelected && !isRefetchingList) || !driver.active) &&
        !isSelected

    const onSelectDriver = () => {
        setDriverSelected(isSelected ? null : driver)
    }

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "1em",
                opacity: isDimmed ? 0.5 : 1,
                width: "100%",
            }}
        >
            <CardHolderAvatar isVehicle={isVehicle} active={driver.active} />
            <Box sx={{width: "8rem"}}>
                <Typography
                    sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "100%",
                    }}
                >
                    {label}
                </Typography>
            </Box>
            <Box
                sx={{
                    position: "relative",
                    marginLeft: "auto",
                }}
            >
                <RowAction
                    isUpdating={isUpdating}
                    isSelected={isSelected}
                    dataCyPrefix={dataCyPrefix}
                    driverId={driver.id}
                    onSelect={onSelectDriver}
                />
            </Box>
            {isSelected && !isUpdating && <CardHolderMenu driver={driver} />}
        </Box>
    )
}
